<template>
  <div class="card" style="min-height: 200px">
    <div class="table-responsive mb-5">
      <table class="table table-bordered">
        <thead>
        <tr>
          <th class="text-center">Photo</th>
          <th class="text-center">Employee Details</th>
          <th class="text-center">Designation</th>
          <th class="text-center">Department</th>
          <th class="text-center">Current Salary</th>
          <th class="text-center">Expected Salary</th>
          <th class="text-center">Status</th>
          <th class="text-center">Offer Action</th>
          <th class="text-center">Appointment Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, i) in tableItems" :key="i">
          <td class="text-center">
            <img
                :src="item.photo"
                :alt="item.name + ' profile photo'"
                class="profile-photo rounded-circle border"
            />
          </td>
          <td class="text-center">{{ item.name }} - {{ item.phone}}</td>
          <td class="text-center">{{ item.designation_name }}</td>
          <td class="text-center">{{ item.department_name }}</td>
          <td class="text-center">{{ item.current_salary }}</td>
          <td class="text-center">{{ item.expected_salary }}</td>
          <td class="text-center text-capitalize">{{ item.application_status_name }}</td>
          <td class="text-center">
            <button v-if="!item.offer_letter_id" class="btn btn-primary btn-sm me-1"
                    @click="goToCreate(item, 'offer_letter')"
                    style="cursor: pointer"
            >
              <i class="fa fa-plus-circle"></i>
            </button>
            <button v-if="item.offer_letter_id" class="btn btn-secondary btn-sm me-1 mb-1 mt-1"
                    @click="goToPrint(item.offer_letter_id)"
                    style="cursor: pointer"
            >
              <i class="fa fa-print"></i>
            </button>
            <button v-if="item.offer_letter_id && item.application_status == 'final_selected'" class="btn btn-primary btn-sm me-1"
                    @click="goToCreate(item, 'offer_letter', item.offer_letter_id)"
                    style="cursor: pointer"
            >
              <i class="fa fa-edit"></i>
            </button>
          </td>
          <td class="text-center">
            <button v-if="!item.appointment_letter_id" class="btn btn-primary btn-sm me-1"
                    @click="goToCreate(item, 'appointment_letter')"
                    style="cursor: pointer"
            >
              <i class="fa fa-plus-circle"></i>
            </button>
            <button v-if="item.appointment_letter_id" class="btn btn-secondary btn-sm me-1"
                    @click="goToPrint(item.appointment_letter_id)"
                    style="cursor: pointer"
            >
              <i class="fa fa-print"></i>
            </button>
            <button title="Edit" v-if="item.appointment_letter_id && item.application_status == 'final_selected'" class="btn btn-primary btn-sm me-1 mt-1 mb-1"
                    @click="goToCreate(item, 'appointment_letter', item.appointment_letter_id)"
                    style="cursor: pointer"
            >
              <i class="fa fa-edit"></i>
            </button>
            <button title="Acceptance" v-if="item.appointment_letter_id" class="btn btn-success btn-sm me-1"
                    @click="goToCreate(item, 'acceptance', item.appointment_letter_id)"
                    style="cursor: pointer"
            >
              <i class="fa fa-check-circle"></i>
            </button>
          </td>
        </tr>
        </tbody>
      </table>
      <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
    </div>

    <div class="px-2 position-absolute bottom-0 mt-5">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>
  </div>
</template>

<script setup>
import Pagination from '@/components/atom/Pagination'
import {inject, onMounted, reactive, ref} from "vue";
import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import appointmentPdfPrinter from '@/services/utils/pdf/appointmentPdfPrinter';
import figureFormatter from '@/services/utils/figureFormatter'

const store = useStore();
const route = useRoute();
const router = useRouter();
const tableItems = ref([])
const loading = ref(false)
const params = ref({
  offset: 20,
});
const showError = inject('showError');
const showSuccess = inject('showSuccess')
const { exportToPDF } = appointmentPdfPrinter();
const emit = defineEmits(['reprocess']);
const { formatDate } = figureFormatter();

const {
  fetchFinalSelectedJobApplications,
  fetchSingleAppointment,
  getEmployeeSalaryStructure
} = handleHrPayroll()

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getJobApplicationList();
}

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}
const getJobApplicationList = async () => {
  let query =`?company_id=${route.params.companyId}`;
  if (route.query.page) query += '&page=' + route.query.page;
  query += '&offset=' + params.value.offset

  loading.value = true
  tableItems.value = []

  await fetchFinalSelectedJobApplications(query).then(res => {
    if (!res.status){
      store.commit("resetPagination")
      return
    }
    tableItems.value = res.data.data;
    setPagination(res.data)
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

async function goToPrint(itemId) {
  try {
    let companyQuery = `?company_id=${route.params.companyId}`;

    const appointmentResponse = await fetchSingleAppointment(itemId, companyQuery);

    if (!appointmentResponse.status) {
      showError("Failed to fetch appointment.");
      return;
    }

    const contactProfileId = appointmentResponse.data.contact_profile_id;
    companyQuery += `&contact_profile_id=${contactProfileId}`;
    const appointmentData = appointmentResponse.data;

    const salaryResponse = await getEmployeeSalaryStructure(companyQuery);

    if (!salaryResponse.status) {
      showError("Failed to fetch salary structure.");
      return;
    }
    const payHeadData = salaryResponse.data;
    exportToPDF(appointmentData, payHeadData);
  } catch (err) {
    console.error("Something went wrong:");
  }
}

const goToCreate = (item, type, appointmentId = null) => {
  let param = {
    companyId: route.params.companyId,
    moduleId: route.params.moduleId,
    menuId: route.params.menuId,
    pageId: route.params.pageId
  }

  let query = route.query;
  query.jobApplicationId = item.id;
  query.type = type;
  if (appointmentId){
    query.appointmentId = appointmentId;
  }
  router.push({
    name:  'offer-letter-add',
    params: param,
    query: query
  })
}

onMounted(() => {
  Promise.all([
    getJobApplicationList()
  ])
})
</script>

<style scoped>
.profile-photo {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border: 2px solid #dee2e6;
}
</style>
